import CustomHtmlKeyboardResponsePlugin from "../../../customPlugin/customHtmlKeyboardResponse";
import { KEY_CODES } from "../../../gearshiftGame/constants";
import { Game } from "../../../gearshiftGame/game";
import { Score } from "../../../gearshiftGame/score";
import { resolveLayoutPlaceholders, applyTutorialHeaderPlaceholders } from "../../../lib/utils";
import { createReadyOverlay, createFollowUpOverlay, createCongratsOverlay } from "../../ui/overlays";
import { createPreservedFrame, prepareBoardPreview } from "./missionHelpers";

/**
 * Shared builder for tutorial timelines that replay selected phases with overlays and crash/wrong-lane loops.
 */
export function buildTutorialWithPhases({
  jsPsych,
  metadata,
  score,
  trialConfig,
  stimulus,
  phases = [1],
  buildersMap = {},
  overlayOptions = {},
  customOptions = {},
  hooks = {},
}) {
  const followUpForward = `[press "__FORWARD_KEY__" to continue ->]`;

  const resolveOverlayText = (text) => resolveLayoutPlaceholders(jsPsych, text);

  const ready = overlayOptions.ready || {};
  const congrats = overlayOptions.congrats || {};
  const followUp = overlayOptions.followUp || {};

  const followUpForwardText =
    followUp.forwardText ?? followUpForward;

  const readyForward =
    ready.forwardText ?? `[press "__FORWARD_KEY__" to begin ->]`;
  const readyBack = ready.backText ?? "";
  const readyImage = ready.imageOrVideoUrl ?? "assets/images/instructions/video_placeholder.svg";
  const readyShowText = ready.showText ?? false;

  const congratsForward =
    congrats.forwardText ?? `[press "__FORWARD_KEY__" to continue ->]`;
  const congratsBack = congrats.backText ?? "";
  const congratsImage = congrats.imageOrVideoUrl ?? "assets/images/instructions/video_placeholder.svg";
  const congratsForwardCrash =
    congrats.forwardTextCrash ?? `[press "__FORWARD_KEY__" to try again ]`;
  const congratsBackCrash = congrats.backTextCrash ?? "";
  const congratsImageCrash =
    congrats.imageOrVideoUrlCrash ?? "assets/images/instructions/try_again_placeholder.svg";
  const congratsShowText = congrats.showText ?? false;
  const skipReadyOverlay = overlayOptions.skipReadyOverlay ?? false;
  const customFollowUpImage = overlayOptions.customFollowUpImage ?? null;
  const followUpOnly = overlayOptions.followUpOnly ?? false;

  const timeline = [];
  const lastPhase = phases[phases.length - 1];
  const firstPhase = phases[0];

  // Helper to create a fresh Game for the current iteration
  // Its on window so other trials in the same iteration can reference the same
  function createFreshGame() {
    // Create a temporary Score for the tutorial so it doesn't affect the real Score
    const tutorialScore = new Score(jsPsych, score.startingLives, score.rewardReduction);

    // Use tutorialScore instead of SCORE
    applyTutorialHeaderPlaceholders(jsPsych);
    window._gear_currentGame = new Game(jsPsych, metadata, tutorialScore, trialConfig);
    window._gear_currentGame.isTutorial = true;
    return window._gear_currentGame;
  }

  // if the first phase is not 0, add a ready overlay at the beginning of other tutorials
  if (firstPhase !== 0) {
    const readyTrial = {
      choices: "NO_KEYS",
      on_load: function () {
        // reset preserved snapshots for this iteration
        window._gear_preservedFrameHTML = null;
        window._gear_preservedFrameHTML_phaseStart = null;
        window._gear_preservedFrameHTML_phaseStart_len = 0;
        // create a fresh game for this iteration since we're not going through Phase 0 (which normally creates necessary game stuff)
        const game = createFreshGame();
        prepareBoardPreview(game, firstPhase);

        // If followUpOnly flag is set, show only the follow-up overlay without ready overlay
        if (followUpOnly || skipReadyOverlay) {
          // If followUpOnly, show the follow-up overlay before starting the game
          if (followUpOnly && !skipReadyOverlay) {
            const followUpOverlay = createFollowUpOverlay({
              ...(customFollowUpImage ? { imageOrVideoUrl: customFollowUpImage } : {}),
              forwardText: resolveOverlayText(followUpForwardText),
              onContinue: () => {
                try {
                  game.startPhase0();
                  if (firstPhase >= 2) {
                    game.matchDriverAndTargetSpeed();
                    if (typeof game.updateCoordinates === "function") {
                      game.updateCoordinates();
                    }
                  }
                } catch (e) {
                  console.error("Error initializing board:", e);
                }
                jsPsych.finishTrial();
              },
            });
            document.body.appendChild(followUpOverlay);
            return;
          }

          try {
            game.startPhase0();
            if (firstPhase >= 2) {
              game.matchDriverAndTargetSpeed();
              if (typeof game.updateCoordinates === "function") {
                game.updateCoordinates();
              }
            }
          } catch (e) {
            console.error("Error initializing board:", e);
          }

          jsPsych.finishTrial();
          return;
        }

        const overlay = createReadyOverlay({
          forwardText: resolveOverlayText(readyForward),
          backText: resolveOverlayText(readyBack),
          imageOrVideoUrl: readyImage,
          showText: readyShowText,
          onStart: () => {
            overlay.remove();

            // Show follow-up overlay first, then start the game
            const followUpOverlay = createFollowUpOverlay({
              ...(customFollowUpImage ? { imageOrVideoUrl: customFollowUpImage } : {}),
              forwardText: resolveOverlayText(followUpForwardText),
              onContinue: () => {
                // start the game after follow-up
                try {
                  game.startPhase0();

                  // if starting from Phase 2+, simulate end-of-Phase-1 state properly
                  if (firstPhase >= 2) {
                    try {
                      // simulate what Phase 1, so the objects start in the correct positions/speeds
                      game.matchDriverAndTargetSpeed();

                      // ensure coordinates are applied immediately
                      if (typeof game.updateCoordinates === "function") {
                        game.updateCoordinates();
                      }

                      // keep default positions to avoid moving other vehicles unexpectedly
                    } catch (e) {
                      console.warn("Failed to simulate Phase 1 state:", e);
                    }
                  }
                } catch (e) {
                  console.error("Error initializing board:", e);
                }
                jsPsych.finishTrial();
              },
            });
            document.body.appendChild(followUpOverlay);
          },
        });

        document.body.appendChild(overlay);
      },
      on_finish: function (data) {
        data.name = "readyOverlay";
      },
    };
    timeline.push(readyTrial);
  }

  for (const p of phases) {
    const builder = buildersMap[p];
    if (!builder) continue;

    // Phase 0 is responsible for creating a fresh game when it runs
    // so repeated iterations will start from a clean state
    if (p === 0) {
      // Build a modified phase0 trial that shows the ready overlay and
      // creates a fresh game when started
      const phase0Trial = {
        choices: "NO_KEYS",
        on_load: function () {
          // create a fresh game for this iteration
          const game = createFreshGame();
          prepareBoardPreview(game, 0);

          // Skip ready overlay if flag is set
          if (followUpOnly || skipReadyOverlay) {
            if (followUpOnly && !skipReadyOverlay) {
              const followUpOverlay = createFollowUpOverlay({
                ...(customFollowUpImage ? { imageOrVideoUrl: customFollowUpImage } : {}),
                forwardText: resolveOverlayText(followUpForwardText),
                onContinue: () => {
                  try {
                    game.startPhase0();
                  } catch (e) {
                    console.error("Error starting phase0:", e);
                  }
                  const phaseTimeout = setTimeout(() => {
                    jsPsych.finishTrial();
                  }, game.targetLaunchDelay);

                  window._gear_phase0_timeout = function () {
                    if (phaseTimeout) clearTimeout(phaseTimeout);
                  };
                },
              });
              document.body.appendChild(followUpOverlay);
              return;
            }

            try {
              game.startPhase0();
            } catch (e) {
              console.error("Error starting phase0:", e);
            }
            // just for the phase to run properly, no need of precision here
            const phaseTimeout = setTimeout(() => {
              jsPsych.finishTrial();
            }, game.targetLaunchDelay);

            window._gear_phase0_timeout = function () {
              if (phaseTimeout) clearTimeout(phaseTimeout);
            };
            return;
          }

          const overlay = createReadyOverlay({
            forwardText: resolveOverlayText(readyForward),
            backText: resolveOverlayText(readyBack),
            imageOrVideoUrl: readyImage,
            showText: readyShowText,
            onStart: () => {
              overlay.remove();

              // Show follow-up overlay first, then start the game 
              const followUpOverlay = createFollowUpOverlay({
                ...(customFollowUpImage ? { imageOrVideoUrl: customFollowUpImage } : {}),
                forwardText: resolveOverlayText(followUpForwardText),
                onContinue: () => {
                  // start the game after follow-up is confirmed
                  try {
                    game.startPhase0();
                  } catch (e) {
                    console.error("Error starting phase0:", e);
                  }

                  const phaseTimeout = setTimeout(() => {
                    jsPsych.finishTrial();
                  }, game.targetLaunchDelay);

                  // store timeout clear function on window in case other code needs to clear it
                  window._gear_phase0_timeout = function () {
                    if (phaseTimeout) clearTimeout(phaseTimeout);
                  };
                },
              });
              document.body.appendChild(followUpOverlay);
            },
          });

          document.body.appendChild(overlay);
        },
        on_finish: function (data) {
          // preserve the visual frame if this is the last requested phase
          try {
            if (p === lastPhase) {
              const display = document.querySelector(".jspsych-display-element") || document.body;
              const preserved = createPreservedFrame({ html: display.innerHTML, display, score });
              document.body.appendChild(preserved);
            }
          } catch (e) {
            console.warn("buildTutorialWithPhases: failed to preserve final frame (phase0):", e);
          }

          // clear any leftover timeout
          try {
            const clearFn = window._gear_phase0_timeout;
            if (typeof clearFn === "function") clearFn();
          } catch (e) {
            console.warn("buildTutorialWithPhases: error clearing phase0 timeout:", e);
          }

          // Reproduce the phase 0 finish data assignments
          applyTutorialHeaderPlaceholders(jsPsych);
          try {
            const game = window._gear_currentGame;
            data.name = "initializeTarget";
            if (game) {
              data.driverSpeed = game.driverCarSpeed;
              data.distanceToTarget = game.driverDistanceToTarget;
              data.trialStart = {
                name: "trialStart",
                driverSpeed: game.driverCarSpeed,
                timestamp: jsPsych.data.get().last(2).values()[0].time_elapsed,
              };
            }
          } catch (e) {
            console.warn("buildTutorialWithPhases: error assigning phase0 finish data:", e);
          }
          if (hooks.onPhaseFinish) hooks.onPhaseFinish(p, data, window._gear_currentGame);
        },
      };

      timeline.push(phase0Trial);
      continue;
    }

    // for other phases, construct trials that use `window._gear_currentGame`
    const phaseTrials = builder({}, jsPsych, score);
    for (const t of phaseTrials) {
      const cloned = Object.assign({}, t);

      // if the template had a trial_duration based on the game, replace it with current game instace data
      // special handling for phase 2 which needs CSI duration
      if (p === 2) {
        cloned.trial_duration = function () {
          const base = window._gear_currentGame ? window._gear_currentGame.csi || 0 : 0;
          // add delay only if explicitly requested and no crash occurred
          const crashed = window._gear_crashed;
          const shouldAddDelay = customOptions.addPhase2Delay && !crashed;
          const hold = shouldAddDelay ? 2000 : 0;
          // ensure we at least wait the base CSI
          return base + hold;
        };
      } else if (t.trial_duration === undefined) {
        // nothing to do
      } else if (typeof t.trial_duration === "number") {
        cloned.trial_duration = t.trial_duration;
      }

      // Replace on_load with a function that calls the correct startPhase
      const parsedOnLoad = function () {
        applyTutorialHeaderPlaceholders(jsPsych);
        try {
          if (!window._gear_currentGame) createFreshGame();
          const game = window._gear_currentGame;
          switch (p) {
            case 1:
              game.startPhase1();
              break;
            case "1.1":
              game.startPhase1_1();
              break;
            case "1.2":
              game.startPhase1_2();
              break;
            case 2:
              game.startPhase2();
              break;
            case 3:
              game.startPhase3();
              break;
            case 4:
              // phase 4 needs the chosen key from previous trial
              game.startPhase4(jsPsych.data.get().last(1).values()[0].keyCode);
              break;
            case 5:
              game.startPhase5();
              break;
            case 7:
              game.startPhase7();
              break;
            default:
              break;
          }

          // Capture snapshot at phase start for fallback.
          try {
            const display = document.querySelector(".jspsych-display-element") || document.body;
            const html = display.innerHTML;
            const len = html?.length || 0;
            if (len >= 200) {
              window._gear_preservedFrameHTML_phaseStart = html;
              window._gear_preservedFrameHTML_phaseStart_len = len;
            }
          } catch (e) {
            console.warn("buildTutorialWithPhases: failed to capture phase start snapshot", e);
          }

          if (hooks.onPhaseStart) hooks.onPhaseStart(p, game);
        } catch (e) {
          console.warn(`Error starting phase ${p}:`, e);
        }
      };

      cloned.on_load = parsedOnLoad;

      // on_finish use runtime game state and preserve frame and bike crash flags
      cloned.on_finish = function (data) {
        try {
          if (p === lastPhase) {
            const display = document.querySelector(".jspsych-display-element") || document.body;
            const preserved = createPreservedFrame({ html: display.innerHTML, display, score });
            const snapshotLength = preserved.innerHTML?.length || 0;
            const previousLength = window._gear_preservedFrameHTML
              ? window._gear_preservedFrameHTML.length
              : 0;
            // If the snapshot is too small, skip replacing what we already have
            if (snapshotLength < 200) {
              // fallback to phase-start snapshot if available
              if (window._gear_preservedFrameHTML_phaseStart_len >= 200) {
                const fallbackPreserved = createPreservedFrame({
                  html: window._gear_preservedFrameHTML_phaseStart,
                  display,
                  score,
                });
                const existing = document.getElementById("gear-preserved-frame");
                if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
                document.body.appendChild(fallbackPreserved);
                window._gear_preservedFrameHTML = fallbackPreserved.innerHTML;
              }
            } else {
              // remove any existing preserved frame so we don't end up with duplicates
              const existing = document.getElementById("gear-preserved-frame");
              if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
              document.body.appendChild(preserved);
              // Also store the HTML snapshot for later, in case the element is removed before congrats on_load
              if (snapshotLength >= previousLength) {
                window._gear_preservedFrameHTML = preserved.innerHTML;
              }
            }
          }

          if (p === 1 && data && data.crashed) {
            try {
              window._gear_crashed = true;
              const display = document.querySelector(".jspsych-display-element") || document.body;
              if (!document.getElementById("gear-preserved-frame")) {
                const preserved = createPreservedFrame({ html: display.innerHTML, display, score });
                document.body.appendChild(preserved);
              }
            } catch (e) {
              console.warn("buildTutorialWithPhases: failed to preserve frame on crash:", e);
            }
          }

          // Phase-specific data assignments using the runtime game instance
          const game = window._gear_currentGame;
          if (game) {
            switch (p) {
              case 1:
                data.name = "engageTarget";
                data.cueCarOn = {
                  name: "cueCarOn",
                  driverSpeed: game.driverCarSpeed,
                  timestamp: jsPsych.data.get().last(1).values()[0].time_elapsed,
                  distanceToTarget: game.driverDistanceToTarget,
                  csi: game.csi,
                };
                break;
              case "1.1":
                // data.name and other fields already set by buildPhase1_1
                data.driverSpeed = game.driverCarSpeed;
                break;
              case "1.2":
                // data.name and other fields already set by buildPhase1_2
                data.driverSpeed = game.driverCarSpeed;
                break;
              case 2:
                data.name = "licensePlateOn";
                data.timestamp = jsPsych.data.get().last(2).values()[0].time_elapsed;
                data.driverSpeed = game.driverCarSpeed;
                data.distanceToTarget = jsPsych.data.get().last(2).values()[0].distanceToTarget;
                break;
              case 3:
                data.name = "responseKeyPressed";
                data.response_keypress_time = data.time_elapsed;
                data.keyCode = KEY_CODES[data.response_event_code ?? data.response] ?? null;
                data.responseTime =
                  data.time_elapsed - jsPsych.data.get().last(2).values()[0].time_elapsed;
                data.driverSpeed = game.driverCarSpeed;
                // displayLicense()/captureLicensePlateOnTime() only run once startPhase3's
                // on_load fires, which is after the "licensePlateOn" (phase 2) row already
                // saved - so this is the earliest row where the value is actually available
                data.license_plate_on_time = game.licensePlateOnTime;
                if (data.crashed || window._gear_bombExplodedTimer) jsPsych.endCurrentTimeline();
                else data.crashed = false;
                break;
              case 4:
                data.name = "driverInResponseLane";
                data.driverSpeed = game.driverCarSpeed;
                // set wrongLane in two ways for reliability
                // global flag (window._gear_wrongLane) for visual response
                // trial data (data.wrongLane) for loop_function
                if (!game.correctLaneChosen) {
                  window._gear_wrongLane = true;
                }
                data.wrongLane = !game.correctLaneChosen;
                break;
              case 5:
                data.name = "packageDisappears";
                data.driverSpeed = game.driverCarSpeed;
                data.emotion = game.emotion;
                data.packageInTargetLane = {
                  timestamp: jsPsych.data.get().last(2).values()[0].time_elapsed,
                  driverSpeed: game.driverCarSpeed,
                  packageDistance: game.packageDistanceToTarget,
                };
                data.targetDisappears = {
                  timestamp: game.targetCarDisappearTimestamp,
                  driverSpeed: game.driverCarSpeed,
                };
                data.emotionReaction = {
                  emotion: game.emotion,
                  driverSpeed: game.driverCarSpeed,
                  timestamp:
                    jsPsych.data.get().last(1).values()[0].time_elapsed - game.feedbackDuration,
                };
                break;
              case 7:
                data.name = "driverAtCenter";
                data.driverToCenter = {
                  timestamp: jsPsych.data.get().last(2).values()[0].time_elapsed,
                  driverSpeed: game.driverCarSpeed,
                };
                data.driverSpeed = game.driverCarSpeed;
                break;
              default:
                break;
            }
          }
        } catch (e) {
          console.warn(`buildTutorialWithPhases: error in phase ${p} on_finish:`, e);
        }

        if (hooks.onPhaseFinish) hooks.onPhaseFinish(p, data, window._gear_currentGame);
      };

      timeline.push(cloned);
    }
  }

  // create a small congrats trial that displays an overlay
  const congratsTrial = {
    type: CustomHtmlKeyboardResponsePlugin,
    stimulus: "",
    choices: "NO_KEYS",
    on_load: () => {
      let pres = document.getElementById("gear-preserved-frame");
      // Fallback: if no preserved frame (success path), try stored HTML snapshot; otherwise capture current display
      if (!pres) {
        try {
          const display = document.querySelector(".jspsych-display-element") || document.body;
          const html = window._gear_preservedFrameHTML || display.innerHTML;
          const preserved = createPreservedFrame({ html, display, score });
          document.body.appendChild(preserved);
          pres = preserved;
        } catch (e) {
          console.warn("buildTutorialWithPhases: failed to preserve frame on success path", e);
          return;
        }
      }

      // get the crash state and wrong lane state
      const crashed = !!window._gear_crashed;
      const wrongLane = !!window._gear_wrongLane;
      const bombExploded = !!window._gear_bombExplodedTimer;
      const tryAgain = crashed || wrongLane || bombExploded;

      // conditional strings/images
      const forwardText = tryAgain ? congratsForwardCrash : congratsForward;
      const backText = tryAgain ? congratsBackCrash : congratsBack;
      const imageOrVideoUrl = tryAgain ? congratsImageCrash : congratsImage;

      // Check fails limit before showing overlay
      if (hooks.onBeforeCongratsOverlay) {
        try {
          const shouldInterrupt = hooks.onBeforeCongratsOverlay({
            tryAgain,
            crashed,
            wrongLane,
            bombExploded,
          });
          if (shouldInterrupt === true) {
            return; // Stop execution, don't show overlay
          }
        } catch (e) {
          console.warn("buildTutorialWithPhases: onBeforeCongratsOverlay failed:", e);
        }
      }

      const overlay = createCongratsOverlay({
        forwardText: resolveOverlayText(forwardText),
        backText: resolveOverlayText(backText),
        imageOrVideoUrl,
        playCelebrate: !tryAgain,
        showText: congratsShowText,
        onContinue: () => {
          // remove preserved frame if present
          const preserved = document.getElementById("gear-preserved-frame");
          if (preserved) preserved.parentNode.removeChild(preserved);

          // clear any timeouts
          if (typeof window._gear_phase0_timeout === "function") window._gear_phase0_timeout();

          // finish the congrats trial, the loop_function will repeat the phases automatically
          // if the last iteration contained a crash.
          jsPsych.finishTrial();
        },
      });

      document.body.appendChild(overlay);
    },
    on_finish: (data) => {
      if (hooks.onPhaseFinish) hooks.onPhaseFinish("congrats", data, window._gear_currentGame);
    },
  };

  timeline.push(congratsTrial);

  // Wrap the phases timeline in a looping node which will repeat the entire
  // set of phases when a crash was detected
  // the loop_function receives the data from the
  // last iteration to decide if needs to run the timeline again
  const node = {
    timeline: [
      {
        type: CustomHtmlKeyboardResponsePlugin,
        stimulus,
        data: { trialConfig, tutorial: true },
        timeline,
      },
    ],
    loop_function: function (data) {
      // if any trial in the previous iteration flagged `crashed` or `wrongLane`, repeat.
      try {
        const values = data.values();
        const crashed = values.some((v) => !!v.crashed);
        const wrongLaneFromData = values.some((v) => !!v.wrongLane);
        const bombExplodedFromData = values.some((v) => !!v.bombExploded);
        const wrongLaneGlobal = !!window._gear_wrongLane;
        const bombExplodedGlobal = !!window._gear_bombExplodedTimer;
        let shouldRepeat =
          crashed ||
          wrongLaneFromData ||
          wrongLaneGlobal ||
          bombExplodedFromData ||
          bombExplodedGlobal;


        if (hooks.onLoopDecide) {
          try {
            const maybe = hooks.onLoopDecide({
              data,
              crashed,
              wrongLaneFromData,
              wrongLaneGlobal,
              bombExplodedFromData,
              bombExplodedGlobal,
              defaultShouldRepeat: shouldRepeat,
            });
            if (typeof maybe === "boolean") shouldRepeat = maybe;
          } catch (e) {
            console.warn("buildTutorialWithPhases: onLoopDecide failed:", e);
          }
        }

        // clear the global flags so the next iteration starts clean
        window._gear_crashed = false;
        window._gear_wrongLane = false;
        window._gear_bombExplodedTimer = false;

        return shouldRepeat;
      } catch (e) {
        console.warn("buildTutorialWithPhases: loop_function error:", e);
        return false;
      }
    },
  };

  return node;
}
